import React, { useState, useEffect } from "react";
import { Container, Table, Button, Modal, Alert } from "react-bootstrap";
import { errorTracker, ErrorLog } from "../../utils/errorTracker";
import "../../App.css";
import "./errors.css";

const ErrorsPage = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    loadErrors();
  }, []);

  const loadErrors = async () => {
    try {
      const errorList = await errorTracker.getErrors();
      console.log("errorList", errorList);
      setErrors(errorList);
    } catch (error) {
      console.error("❌ ErrorsPage: Failed to load errors:", error);
    }
  };

  const handleErrorClick = (error: ErrorLog) => {
    setSelectedError(error);
    setShowModal(true);
  };

  const handleDeleteError = async (errorId: string) => {
    try {
      await errorTracker.deleteError(errorId);
      await loadErrors();
    } catch (error) {
      console.error("❌ ErrorsPage: Failed to delete error:", error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("da-DK");
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    return message.length > maxLength
      ? message.substring(0, maxLength) + "..."
      : message;
  };

  return (
    <>
      <Container className="mainPage">
        <div className="errors-header">
          <h1>Error Log</h1>
          <div className="errors-actions">
            {/* <Button
              variant="outline-danger"
              onClick={() => setShowClearConfirm(true)}
              disabled={errors.length === 0}
            >
              Clear All Errors
            </Button> */}
            <Button variant="outline-secondary" onClick={loadErrors}>
              Refresh
            </Button>
          </div>
        </div>

        {errors.length === 0 ? (
          <Alert variant="success">
            <Alert.Heading>No Errors Found</Alert.Heading>
            <p>Great! No errors have been logged in the application.</p>
          </Alert>
        ) : (
          <div className="errors-table-container">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Timestamp</th>
                  <th>Component</th>
                  <th>Error Type</th>
                  <th>Error Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((error) => (
                  <tr key={error.id}>
                    <td>
                      <code>{error.id}</code>
                    </td>
                    <td>{formatTimestamp(error.timestamp)}</td>
                    <td>{error.component}</td>
                    <td>{error.error_type}</td>
                    <td
                      className="error-message-cell"
                      onClick={() => handleErrorClick(error)}
                    >
                      {truncateMessage(error.error_message)}
                    </td>
                    <td>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleErrorClick(error)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteError(error.id)}
                        className="ms-1"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Error Details Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Error Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedError && (
              <div>
                <div className="error-detail-row">
                  <strong>ID:</strong> <code>{selectedError.id}</code>
                </div>
                <div className="error-detail-row">
                  <strong>Timestamp:</strong>{" "}
                  {formatTimestamp(selectedError.timestamp)}
                </div>
                <div className="error-detail-row">
                  <strong>Component:</strong> {selectedError.component}
                </div>
                <div className="error-detail-row">
                  <strong>Error Type:</strong> {selectedError.error_type}
                </div>
                <div className="error-detail-row">
                  <strong>Error Message:</strong>
                  <div className="error-message">
                    {selectedError.error_message}
                  </div>
                </div>
                {selectedError.apiUrl && (
                  <div className="error-detail-row">
                    <strong>API URL:</strong>{" "}
                    <code>{selectedError.apiUrl}</code>
                  </div>
                )}
                {selectedError.userId && (
                  <div className="error-detail-row">
                    <strong>User ID:</strong> {selectedError.userId}
                  </div>
                )}
                {selectedError.email && (
                  <div className="error-detail-row">
                    <strong>Email:</strong> {selectedError.email}
                  </div>
                )}
                {selectedError.additionalData &&
                  Object.keys(selectedError.additionalData).length > 0 && (
                    <div className="error-detail-row">
                      <strong>Additional Data:</strong>
                      <pre className="error-additional-data">
                        {JSON.stringify(selectedError.additionalData, null, 2)}
                      </pre>
                    </div>
                  )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            {selectedError && (
              <Button
                variant="danger"
                onClick={() => {
                  handleDeleteError(selectedError.id);
                  setShowModal(false);
                }}
              >
                Delete Error
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Clear All Confirmation Modal */}
        <Modal
          show={showClearConfirm}
          onHide={() => setShowClearConfirm(false)}
        >
          <Modal.Header closeButton>
            {/* <Modal.Title>Confirm Clear All Errors</Modal.Title> */}
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to clear all {errors.length} errors? This
            action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowClearConfirm(false)}
            >
              Cancel
            </Button>
            {/* <Button variant="danger" onClick={handleClearAllErrors}>
              Clear All Errors
            </Button> */}
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default ErrorsPage;
